import React from 'react'
import { useI18n } from '../../i18n'
import type { BlockerSeverity } from './auditReadinessModel'
import styles from '../../styles/components/assets/AssetsInformationSystems.module.scss'

function clampPercent(n: number | null | undefined): number | null {
  if (n == null || !Number.isFinite(n)) return null
  return Math.max(0, Math.min(100, Math.round(n)))
}

function ringTone(status: string): string {
  const s = String(status || 'UNKNOWN').toUpperCase()
  if (s.includes('CRITICAL')) return styles.readinessRingCritical
  if (s === 'BLOCKED') return styles.readinessRingNotReady
  if (s.includes('NOT') || s.includes('NEEDS')) return styles.readinessRingNotReady
  if (s === 'READY_FOR_REVIEW' || s === 'REVIEW') return styles.readinessRingReview
  if (s === 'READY') return styles.readinessRingReady
  return styles.readinessRingUnknown
}

function blockerClass(severity: BlockerSeverity, blockers: number): string {
  if (blockers <= 0) return ''
  if (severity === 'CRITICAL') return styles.readinessRingBlockersCritical
  if (severity === 'HIGH') return styles.readinessRingBlockersHigh
  return styles.readinessRingBlockersMedium
}

interface Props {
  percent: number | null | undefined
  status: string
  statusLabel?: string
  blockers?: number
  blockerSeverity?: BlockerSeverity
  sourceHint?: string
  unknownPosture?: boolean
  size?: 'sm' | 'md' | 'lg'
  onClick?: () => void
  className?: string
}

export default function ReadinessRing({
  percent,
  status,
  statusLabel,
  blockers = 0,
  blockerSeverity = 'NONE',
  sourceHint,
  unknownPosture = false,
  size = 'md',
  onClick,
  className = '',
}: Props) {
  const { t } = useI18n()
  const p = clampPercent(percent)
  const tone = ringTone(status)
  const sizeClass =
    size === 'lg' ? styles.readinessRingLg : size === 'sm' ? styles.readinessRingSm : styles.readinessRingMd
  const label =
    statusLabel ||
    t(`assets.auditReadiness.status.${status}`, t(`assets.grc.readiness.${status}`, status))

  const ring = (
    <span
      className={`${styles.readinessRingOuter} ${sizeClass} ${tone} ${p == null ? styles.readinessRingNa : ''}`}
      style={p != null ? ({ '--ring-pct': `${p}%` } as React.CSSProperties) : undefined}
      aria-hidden
    >
      <span className={styles.readinessRingInner}>
        <span className={styles.readinessRingValue}>
          {p != null ? `${p}%` : t('assets.postureRow.notAvailable', 'N/A')}
        </span>
      </span>
    </span>
  )

  const inner = (
    <>
      {ring}
      <span className={styles.readinessRingStatus}>
        {unknownPosture
          ? t('assets.auditReadiness.unknownPosture', 'Unknown posture')
          : label}
      </span>
      {blockers > 0 ? (
        <span
          className={`${styles.readinessRingBlockers} ${blockerClass(blockerSeverity, blockers)}`.trim()}
        >
          {blockers} {t('assets.auditReadiness.blockers', 'blockers')}
        </span>
      ) : null}
      {sourceHint ? <span className={styles.readinessRingSource}>{sourceHint}</span> : null}
    </>
  )

  if (onClick) {
    return (
      <button
        type="button"
        className={`${styles.readinessRingWidget} ${className}`.trim()}
        onClick={onClick}
        aria-label={t('assets.postureRow.openReadiness', 'Open readiness')}
      >
        {inner}
      </button>
    )
  }

  return <span className={`${styles.readinessRingWidget} ${className}`.trim()}>{inner}</span>
}
